/**
 * Author: Claude Code
 * Generation: Navigation bar with Suno-style animations and i18n support
 * Version: v3.2.125
 * Modified: 2025-10-15 (Fixed i18n translations + removed redundant Balance display)
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { LogIn, UserPlus, User, LogOut } from 'lucide-react';
import { useAuthStore } from '@store/AuthStore';
import LanguageSwitcher from './LanguageSwitcher';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Navbar() {
  const { t } = useTranslation();
  const { isAuthenticated, user, openAuthModal, logout } = useAuthStore();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleLogout = async () => {
    await logout();
    setShowDropdown(false);
    navigate('/');
  };

  return (
    <nav className="navbar">
      <div className="navbar-left">
        <div className="navbar-brand" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img src="/favicon-96x96.png" alt="Agent Battle Royale" style={{ height: '26px', width: '26px' }} />
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', fontWeight: 700 }}>
            <span style={{ color: '#ffffff' }}>Agent</span>
            <span style={{ color: 'rgb(147, 147, 147)' }}>Battle</span>
            <span style={{ color: 'rgb(208, 9, 11)' }}>Royale</span>
          </div>
        </div>
        <div className="navbar-nav">
          <Link to="/" className="nav-link" style={{ color: '#ffffff' }}>{t('navbar.home')}</Link>
          <a href="#" className="nav-link" style={{ color: '#ffffff' }}>{t('navbar.structure')}</a>
          <a href="#" className="nav-link" style={{ color: '#ffffff' }}>{t('navbar.socialMedia')}</a>
        </div>
      </div>
      <div className="user-section">
        {/* v3.2.54: Language switcher */}
        <LanguageSwitcher />
        {!isAuthenticated ? (
          <div id="guestSection">
            {/* v3.2.58: Sign Up with ghost style, unified shape */}
            <motion.button
              className="btn btn-ghost btn-sm"
              onClick={() => openAuthModal('signup')}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <UserPlus className="btn-icon" />
              {t('buttons.signUp')}
            </motion.button>

            {/* v3.2.54: Sign In with i18n support */}
            <motion.button
              className="btn btn-primary btn-sm"
              onClick={() => openAuthModal('signin')}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <LogIn className="btn-icon" />
              {t('buttons.signIn')}
            </motion.button>
          </div>
        ) : (
          <div id="userSection" ref={dropdownRef} style={{ position: 'relative' }}>
            <div className="user-badge" onClick={() => setShowDropdown(!showDropdown)} style={{ cursor: 'pointer' }}>
              <div className="user-avatar">{user?.username?.[0]?.toUpperCase() || 'T'}</div>
              <span id="userName">{user?.username || 'Thunder'}</span>
              <div className="coin-badge">
                <span>💎</span>
                <span id="userCoins">{user?.virtualBalance?.toLocaleString() || '1,250'}</span>
              </div>
            </div>

            <AnimatePresence>
              {showDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '8px',
                    background: 'rgba(20, 20, 30, 0.95)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    padding: '8px 0',
                    minWidth: '160px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    zIndex: 1000
                  }}
                >
                  <div
                    style={{
                      padding: '10px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <User size={18} />
                    <span>{t('navbar.profile')}</span>
                  </div>

                  <div style={{ height: '1px', background: 'rgba(255, 255, 255, 0.1)', margin: '8px 0' }} />

                  <div
                    onClick={handleLogout}
                    style={{
                      padding: '10px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      color: '#ff4757',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 71, 87, 0.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <LogOut size={18} />
                    <span>{t('buttons.logout')}</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </nav>
  );
}
